import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDemoState } from "@/data/demoState";
import {
  Shield, LayoutDashboard, Mail, FileText, FileUp, ShieldCheck,
  Inbox, Lock, Package, CheckSquare, Eye, FileCheck, Users,
  ChevronLeft, ChevronRight, HardHat, ClipboardCheck, Truck, Search, Settings, Sparkles,
  AlertTriangle, Calendar, BookOpen, MessageSquare, Send, FileBarChart, Gift
} from "lucide-react";
import RoleLensSelector from "@/components/shared/RoleLensSelector";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const builderLinks = [
  { path: "/builder", label: "Overview", icon: LayoutDashboard },
  { path: "/builder/project-intake", label: "Create Project", icon: FileUp },
  { path: "/builder/new-mail", label: "New Approvals Mail", icon: Mail },
  { path: "/builder/rfi-war-room", label: "Council Requests", icon: FileText },
  { path: "/builder/decision-import", label: "Decision Import", icon: FileUp },
  { path: "/builder/stage-conditions", label: "Stage Conditions", icon: ShieldCheck },
  { path: "/builder/proof-review", label: "Proof Review", icon: Inbox },
  { path: "/builder/stage-readiness", label: "Stage Readiness", icon: Lock },
  { path: "/builder/inspection-readiness", label: "Inspection Readiness", icon: Search },
  { path: "/builder/job-blocker", label: "Job Blocker", icon: AlertTriangle },
  { path: "/builder/job-blocker-copilot", label: "Blocker Copilot", icon: Sparkles },
  { path: "/builder/trade-release", label: "Trade Release", icon: Truck },
  { path: "/builder/release-calendar", label: "Release Calendar", icon: Calendar },
  { path: "/builder/site-ready-pack", label: "Site Ready Pack", icon: Package },
  { path: "/builder/pre-submission-check", label: "Handover Control", icon: CheckSquare },
  { path: "/builder/scope-boundary-ledger", label: "Scope Boundary", icon: BookOpen },
  { path: "/builder/readiness-dossier", label: "Readiness Dossier", icon: FileBarChart },
  { path: "/builder/whatsapp-action-rail", label: "WhatsApp Rail", icon: MessageSquare },
  { path: "/builder/email-action-rail", label: "Email Rail", icon: Send },
];

const surveyorLinks = [
  { path: "/surveyor", label: "Overview", icon: LayoutDashboard },
  { path: "/surveyor/incoming-packs", label: "Incoming Packs", icon: Package },
  { path: "/surveyor/review-centre", label: "Review Centre", icon: Eye },
  { path: "/surveyor/closeout-view", label: "Closeout View", icon: FileCheck },
];

const sharedLinks = [
  { path: "/shared/client-portal", label: "Client Portal", icon: Users },
  { path: "/shared/buyer-handover", label: "Buyer Handover", icon: Gift },
];

export default function AppSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { workspace } = useDemoState();

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    const linkContent = (
      <Link
        to={item.path}
        data-testid={`sidebar-${item.path.replace(/\//g, '-').slice(1)}`}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
          isActive
            ? "text-white"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        )}
        style={isActive ? { backgroundColor: workspace.accentColor } : undefined}
      >
        <Icon className={cn("w-[18px] h-[18px] shrink-0", isActive ? "text-white" : "text-slate-400")} />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right" className="font-body text-xs">{item.label}</TooltipContent>
        </Tooltip>
      );
    }
    return linkContent;
  };

  return (
    <TooltipProvider>
      <aside
        data-testid="app-sidebar"
        className={cn(
          "h-screen bg-white border-r border-slate-200 flex flex-col sidebar-transition fixed left-0 top-0 z-40",
          collapsed ? "w-[60px]" : "w-[240px]"
        )}
      >
        {/* Workspace-branded header */}
        <div className="h-14 flex items-center px-3 border-b border-slate-100 gap-2.5">
          <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="sidebar-logo">
            <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 text-white text-[10px] font-bold"
              style={{ backgroundColor: workspace.accentColor }}>
              {workspace.logoInitials}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <span className="font-heading font-semibold text-sm text-slate-900 tracking-tight block truncate">{workspace.name}</span>
                <span className="text-[9px] text-slate-400 block truncate">easypermit</span>
              </div>
            )}
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {!collapsed && (
            <div className="px-3 py-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <HardHat className="w-3 h-3" /> Builder
              </span>
            </div>
          )}
          {builderLinks.map((item) => <NavItem key={item.path} item={item} />)}

          <div className="py-2 px-3"><Separator /></div>

          {!collapsed && (
            <div className="px-3 py-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <ClipboardCheck className="w-3 h-3" /> Surveyor
              </span>
            </div>
          )}
          {surveyorLinks.map((item) => <NavItem key={item.path} item={item} />)}

          <div className="py-2 px-3"><Separator /></div>

          {!collapsed && (
            <div className="px-3 py-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Shared</span>
            </div>
          )}
          {sharedLinks.map((item) => <NavItem key={item.path} item={item} />)}
        </div>

        <div className="border-t border-slate-100 p-2 space-y-1">
          <Link
            to="/builder/setup"
            className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-colors"
            data-testid="sidebar-setup-link"
          >
            <Settings className="w-4 h-4" />
            {!collapsed && "Setup"}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-collapse-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-colors"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            {!collapsed && "Collapse"}
          </button>
        </div>

        {!collapsed && (
          <div className="px-3 py-2 border-t border-slate-100">
            <RoleLensSelector compact />
          </div>
        )}

        {!collapsed && (
          <div className="px-4 py-2 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 leading-tight">Concept demo. Guidance only. You still lodge.</p>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}
